'use client';

import { useState, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';

interface NewsletterSignupProps {
  variant: 'inline' | 'card' | 'sidebar';
  source?: string;
}

export default function NewsletterSignup({ variant, source }: NewsletterSignupProps) {
  const t = useTranslations('newsletter');
  const locale = useLocale();

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'duplicate'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail || status === 'loading') return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale, source: source || variant }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      if (data.alreadyExists) {
        setStatus('duplicate');
      } else {
        setStatus('success');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : t('error'));
    }
  };

  // Shared gradient mesh background
  const meshBg = (
    <div className="absolute inset-0 overflow-hidden rounded-[inherit] pointer-events-none" aria-hidden="true">
      <div className="absolute -top-1/2 -right-1/4 w-[60%] h-[200%] rounded-full bg-primary/[0.15] blur-3xl" />
      <div className="absolute -bottom-1/3 -left-1/4 w-[50%] h-[150%] rounded-full bg-primary/[0.12] blur-3xl" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
    </div>
  );

  // Success state
  if (status === 'success' || status === 'duplicate') {
    const message = status === 'duplicate' ? t('duplicate') : t('success');
    return (
      <div className={wrapperClass(variant)}>
        <div className={containerClass(variant)}>
          {meshBg}
          <div className="relative z-10 flex flex-col items-center justify-center text-center py-2">
            <div className="relative mb-3">
              <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-lg animate-pulse" />
              <CheckCircle className="relative w-10 h-10 text-emerald-400 animate-[scaleIn_0.35s_ease-out]" />
            </div>
            <p className="text-foreground font-medium text-sm">{message}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── INLINE variant ───────────────────────────────────────────────
  if (variant === 'inline') {
    return (
      <div className={wrapperClass(variant)}>
        <div className={containerClass(variant)}>
          {meshBg}
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
            {/* Copy */}
            <div className="flex-1 min-w-0 text-center md:text-left">
              <h3 className="font-display text-xl md:text-2xl font-bold text-foreground tracking-tight mb-1.5">
                {t('title')}
              </h3>
              <p className="text-foreground/90 text-sm leading-relaxed max-w-md">
                {t('subtitle')}
              </p>
            </div>
            {/* Form */}
            <form onSubmit={handleSubmit} className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <input
                  ref={inputRef}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                  placeholder={t('placeholder')}
                  required
                  className="w-full h-11 px-4 bg-background/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50"
                  aria-label={t('placeholder')}
                />
              </div>
              <button
                type="submit"
                disabled={!isValidEmail || status === 'loading'}
                className="group relative h-11 px-6 rounded-lg bg-primary text-primary-foreground text-sm font-semibold whitespace-nowrap transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
                <span className="relative flex items-center gap-2">
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : null}
                  {status === 'loading' ? t('subscribing') : t('button')}
                </span>
              </button>
            </form>
          </div>
          {/* Error / Privacy */}
          <div className="relative z-10 mt-3 flex items-center justify-center md:justify-end gap-4">
            {status === 'error' && (
              <span className="flex items-center gap-1.5 text-xs text-red-400">
                <AlertCircle className="w-3.5 h-3.5" />
                {errorMsg || t('error')}
              </span>
            )}
            <span className="text-xs text-muted-foreground">{t('privacy')}</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── CARD variant ─────────────────────────────────────────────────
  if (variant === 'card') {
    return (
      <div className={wrapperClass(variant)}>
        <div className={containerClass(variant)}>
          {meshBg}
          <div className="relative z-10">
            <h3 className="font-display text-lg font-bold text-foreground tracking-tight mb-1.5">
              {t('title')}
            </h3>
            <p className="text-foreground/90 text-sm leading-relaxed mb-5">
              {t('subtitle')}
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                ref={inputRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                placeholder={t('placeholder')}
                required
                className="w-full h-10 px-4 bg-background/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50"
                aria-label={t('placeholder')}
              />
              <button
                type="submit"
                disabled={!isValidEmail || status === 'loading'}
                className="group relative w-full h-10 rounded-lg bg-primary text-primary-foreground text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
                <span className="relative flex items-center justify-center gap-2">
                  {status === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {status === 'loading' ? t('subscribing') : t('button')}
                </span>
              </button>
            </form>
            {status === 'error' && (
              <span className="flex items-center gap-1.5 text-xs text-red-400 mt-2">
                <AlertCircle className="w-3.5 h-3.5" />
                {errorMsg || t('error')}
              </span>
            )}
            <p className="text-xs text-muted-foreground mt-3 text-center">{t('privacy')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── SIDEBAR variant ──────────────────────────────────────────────
  return (
    <div className={wrapperClass(variant)}>
      <div className={containerClass(variant)}>
        {meshBg}
        <div className="relative z-10">
          <p className="text-sm font-medium text-foreground mb-1">{t('title')}</p>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            {t('subtitle')}
          </p>
          <form onSubmit={handleSubmit} className="space-y-2">
            <input
              ref={inputRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
              placeholder={t('placeholder')}
              required
              className="w-full h-10 px-3 bg-background/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50"
              aria-label={t('placeholder')}
            />
            <button
              type="submit"
              disabled={!isValidEmail || status === 'loading'}
              className="group relative w-full h-10 rounded-lg bg-primary text-primary-foreground text-xs font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
              <span className="relative flex items-center justify-center gap-2">
                {status === 'loading' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {status === 'loading' ? t('subscribing') : t('button')}
              </span>
            </button>
          </form>
          {status === 'error' && (
            <span className="flex items-center gap-1.5 text-xs text-red-400 mt-2">
              <AlertCircle className="w-3.5 h-3.5" />
              {errorMsg || t('error')}
            </span>
          )}
          <p className="text-[11px] text-muted-foreground mt-2">{t('privacy')}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Style helpers ────────────────────────────────────────────────
function wrapperClass(variant: string): string {
  switch (variant) {
    case 'inline':
      return 'w-full px-6 py-4';
    case 'card':
      return 'w-full';
    case 'sidebar':
      return 'mt-6';
    default:
      return '';
  }
}

function containerClass(variant: string): string {
  const base = 'relative bg-card border border-border overflow-hidden';
  switch (variant) {
    case 'inline':
      return `${base} max-w-7xl mx-auto rounded-2xl p-8 md:p-10`;
    case 'card':
      return `${base} rounded-2xl p-6`;
    case 'sidebar':
      return `${base} rounded-xl p-4`;
    default:
      return base;
  }
}
